import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {
  IGetResourceItemsRequestInterfaces,
} from '@toolify/server/src/controllers/ResourceItemController/validators/GetResourceItems/types/IGetResourceItemsRequestInterfaces'
import {mapResourceItemResponseInstance} from '@toolify/client/src/modules/api/singletons/mapResourceItemResponseInstance'
import {
  IGetResourceAccessSummariesRequestInterfaces,
} from '@toolify/server/src/controllers/ResourceItemController/validators/GetResourceAccessSummaries/types/IGetResourceAccessSummariesRequestInterfaces'
import {
  IUpdateResourceItemPositionRequestInterfaces,
} from '@toolify/server/src/controllers/ResourceItemController/validators/UpdateResourceItemPosition/types/IUpdateResourceItemPositionRequestInterfaces'
import {
  IToggleResourceItemFavouriteRequestInterfaces,
} from '@toolify/server/src/controllers/ResourceItemController/validators/ToggleResourceItemFavourite/types/IToggleResourceItemFavouriteRequestInterfaces'
import {
  IUpdateEntityResourceItemRequestInterfaces,
} from '@toolify/server/src/controllers/ResourceItemController/validators/UpdateEntityResourceItem/types/IUpdateEntityResourceItemRequestInterfaces'
import {
  IUpdateResourceItemIconRequestInterfaces,
} from '@toolify/server/src/controllers/ResourceItemController/validators/UpdateResourceItemIcon/types/IUpdateResourceItemIconRequestInterfaces'
import {
  IMarkResourceAsReadRequestInterfaces,
} from '@toolify/server/src/controllers/ResourceItemController/validators/MarkResourceAsRead/types/IMarkResourceAsReadRequestInterfaces'
import {
  IMarkResourceAsUnreadRequestInterfaces,
} from '@toolify/server/src/controllers/ResourceItemController/validators/MarkResourceAsUnread/types/IMarkResourceAsUnreadRequestInterfaces'
import {
  IMarkResourceVisitEndedRequestInterfaces,
} from '@toolify/server/src/controllers/ResourceItemController/validators/MarkResourceVisitEnded/types/IMarkResourceVisitEndedRequestInterfaces'
import {IParticularColor} from '@toolify/server/src/services/ColorService/types/IParticularColor'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'

export const useResourceItemApiStore = defineStore('resourceItemApi', {
  actions: {
    async getResourceItems(
      workspaceId: string,
    ): Promise<IGetResourceItemsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {resourceItems} = await apiRequestStore.get<IGetResourceItemsRequestInterfaces>(`/workspace/${workspaceId}/resource-items`)
      return {
        resourceItems: resourceItems.map(activityItem => mapResourceItemResponseInstance(activityItem)),
      }
    },

    async getResourceAccessSummaries(
      workspaceId: string,
      resources: IGetResourceAccessSummariesRequestInterfaces['body']['resources'],
    ): Promise<IGetResourceAccessSummariesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IGetResourceAccessSummariesRequestInterfaces>(`/workspace/${workspaceId}/resource-items/access-summaries`, {
        data: {
          resources,
        },
      })
    },

    async toggleResourceItemFavourite(
      resourceType: string,
      resourceId: string,
      isFavourite: boolean,
    ): Promise<IToggleResourceItemFavouriteRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const response = await apiRequestStore.post<IToggleResourceItemFavouriteRequestInterfaces>(`/resource/${resourceType}/${resourceId}/favourite`, {
        data: {
          isFavourite,
        },
      })
      return response
    },

    async updateResourceItemPosition(
      resourceItemId: string,
      position: number,
      resourceGroupId?: string,
      resourceSpaceId?: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<IUpdateResourceItemPositionRequestInterfaces>(`/resource-item/${resourceItemId}/position`, {
        data: {
          resourceGroupId,
          position,
          resourceSpaceId,
        },
      })
    },

    async deleteResourceItem(
      resourceItemId: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.delete(`/resource-item/${resourceItemId}`)
    },

    async updateResourceItemName(
      resourceItemId: string,
      name: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put(`/resource-item/${resourceItemId}/name`, {
        data: {
          name,
        },
      })
    },

    async updateResourceItemIcon(
      resourceItemId: string,
      iconNameType: IconNameType | null,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<IUpdateResourceItemIconRequestInterfaces>(`/resource-item/${resourceItemId}/icon`, {
        data: {
          iconNameType,
        },
      })
    },
    
    async markResourceAsRead(
      resourceType: string,
      resourceId: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.post<IMarkResourceAsReadRequestInterfaces>(`/resource/${resourceType}/${resourceId}/mark-as-read`)
    },

    async markResourceAsUnread(
      resourceType: string,
      resourceId: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.post<IMarkResourceAsUnreadRequestInterfaces>(`/resource/${resourceType}/${resourceId}/mark-as-unread`)
    },

    async markResourceVisitEnded(
      resourceType: string,
      resourceId: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.post<IMarkResourceVisitEndedRequestInterfaces>(`/resource/${resourceType}/${resourceId}/mark-visit-ended`)
    },

    async moveResourceItem(
      resourceGroupId: string,
      resourceSpaceId: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<IUpdateResourceItemPositionRequestInterfaces>(`/resource-group/${resourceGroupId}/move`, {
        data: {
          resourceSpaceId,
        },
      })
    },

    async updateEntityResourceItem(
      resourceType: string,
      resourceId: string,
      position?: number,
      color?: IParticularColor,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.patch<IUpdateEntityResourceItemRequestInterfaces>(`/resource/${resourceType}/${resourceId}/entity-resource-item`, {
        data: {
          position,
          color,
        },
      })
    },
  },
})
